import { countOccupants, areOccupancyArraysEqual } from 'lib/offer/occupancyUtils'
import type { TripPlannerSurfaceActivityEntry } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityEntryTypes'
import { boundActivityDisplayText } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityDisplayText'

// Hand-built English, not FormattedOccupancy/react-intl -- activity displayText is LLM-context
// only, never customer-facing, and stays English regardless of locale, same rule as every other
// kind in this family (e.g. buildItemStatusChangedActivityEntry's englishStatusLabel).
//
// Includes childrenAge, not just the children count -- the occupancy picker's childVariant is
// "child-age" (ages are directly editable), and areOccupancyArraysEqual (the no-op gate below)
// already compares them. Without ages here, a child's age moving between pricing-relevant bands
// (e.g. 4 -> 14) would pass the gate as a real change but render byte-identical text to the
// pre-change state -- the one thing that actually changed would be invisible to the model, and
// svc-trip's render-time compaction (kind + displayText) could even merge the two occurrences away.
function summarizeOccupancy(rooms: Array<App.Occupants>): string {
  const { adults, children, infants } = countOccupants(rooms)
  const childAges = rooms.flatMap(room => room.childrenAge ?? [])
  const childrenText = children > 0
    ? `${children} ${children === 1 ? 'child' : 'children'}${childAges.length > 0 ? ` (age${childAges.length === 1 ? '' : 's'} ${childAges.join(', ')})` : ''}`
    : undefined
  const parts = [
    adults > 0 ? `${adults} ${adults === 1 ? 'adult' : 'adults'}` : undefined,
    childrenText,
    infants > 0 ? `${infants} ${infants === 1 ? 'infant' : 'infants'}` : undefined,
  ].filter((part): part is string => part !== undefined)
  const roomLabel = `${rooms.length} ${rooms.length === 1 ? 'room' : 'rooms'}`
  return `${parts.join(', ')} (${roomLabel})`
}

// Returns undefined for a no-op apply (the Apply button has no disabled state to prevent this at
// the UI layer) -- areOccupancyArraysEqual is the same occupancy-comparison utility already used
// elsewhere in the codebase, so "unchanged" here means the same thing it means everywhere else
// (undefined/0/empty-array treated as equivalent), not a new ad-hoc definition of equality.
export function buildCarouselOccupancyFilterAppliedActivityEntry(
  carouselTitle: string,
  previousRooms: Array<App.Occupants>,
  newRooms: Array<App.Occupants>,
): TripPlannerSurfaceActivityEntry | undefined {
  if (areOccupancyArraysEqual(previousRooms, newRooms)) return undefined

  return {
    kind: 'carousel_filter_applied',
    label: 'Adjusted carousel filter',
    displayText: boundActivityDisplayText(`Adjusted occupancy filter for "${carouselTitle}" to ${summarizeOccupancy(newRooms)}`),
    sourcePath: 'trip_planner.chat.carousel_filter',
    dataClassification: 'personalised_surface',
    occurredAt: new Date().toISOString(),
  }
}
